import IconGenerator from "@/components/shared/IconGenerator";
import type { SelectProps } from "antd";
import { Flex, Select, Tooltip } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export enum TaskItemPriorityEnum {
  Select = 0,
  High = 1,
  Medium = 2,
  Low = 3,
}

type PriorityColor = "secondary" | "danger" | "warning" | "primary";

type PriorityOption = {
  value: TaskItemPriorityEnum;
  label: string;
  color: PriorityColor;
};

type PrioritySelectProps = Omit<
  SelectProps<TaskItemPriorityEnum>,
  "options" | "children"
> & {
  hasSelectedLabel?: boolean;
  showSelectOption?: boolean;
};

const priorityIconColor: Record<PriorityColor, string> = {
  secondary: "var(--color-disabled)",
  danger: "var(--color-error)",
  warning: "var(--color-warning)",
  primary: "var(--color-primary)",
};

const renderPriorityIcon = (color: PriorityColor) => (
  <IconGenerator name="UilFlag" size={16} color={priorityIconColor[color]} />
);

const PrioritySelect = ({
  variant = "outlined",
  allowClear = false,
  hasSelectedLabel = true,
  showSelectOption = false,
  placeholder,
  style,
  ...props
}: PrioritySelectProps) => {
  const { t } = useTranslation();

  const priorityOptions = useMemo<PriorityOption[]>(() => {
    const options: PriorityOption[] = [
      {
        value: TaskItemPriorityEnum.High,
        label: t("recap.highPriority"),
        color: "danger",
      },
      {
        value: TaskItemPriorityEnum.Medium,
        label: t("recap.mediumPriority"),
        color: "warning",
      },
      {
        value: TaskItemPriorityEnum.Low,
        label: t("recap.lowPriority"),
        color: "primary",
      },
    ];

    if (!allowClear && showSelectOption) {
      return [
        {
          value: TaskItemPriorityEnum.Select,
          label: t("general.select"),
          color: "secondary",
        },
        ...options,
      ];
    }

    return options;
  }, [allowClear, showSelectOption, t]);

  return (
    <Select<TaskItemPriorityEnum>
      allowClear={allowClear}
      optionLabelProp="children"
      popupMatchSelectWidth={false}
      showSearch={false}
      suffixIcon={
        variant === "borderless" ? null : (
          <IconGenerator
            name="UilAngleDown"
            size={18}
            color="var(--color-disabled)"
          />
        )
      }
      placeholder={placeholder ?? t("recap.selectPriority")}
      variant={variant}
      style={{ width: "100%", ...style }}
      styles={{
        popup: {
          root: {
            minWidth: 160,
          },
        },
      }}
      options={priorityOptions.map((option) => ({
        value: option.value,
        label: (
          <Flex align="center" gap={6}>
            {renderPriorityIcon(option.color)}
            {option.label}
          </Flex>
        ),
        children: (
          <Tooltip title={option.label}>
            <Flex align="center" gap={6}>
              {renderPriorityIcon(option.color)}
              {hasSelectedLabel ? option.label : null}
            </Flex>
          </Tooltip>
        ),
      }))}
      {...props}
    />
  );
};

export default PrioritySelect;
